// Device detection utilities for the AlphaMind IDO Frontend logging system
import { DeviceInfo, DeviceType, WalletType, NetworkInfo, WalletInfo } from '../types/logging.types';

// Generate unique session ID
export const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Generate unique log ID
export const generateLogId = (): string => {
  return `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Detect device type based on user agent and screen size
export const detectDeviceType = (): DeviceType => {
  const userAgent = navigator.userAgent.toLowerCase();
  const screenWidth = window.screen.width;
  
  // Mobile detection
  if (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
    return screenWidth < 768 ? 'mobile' : 'tablet';
  }
  
  // Tablet detection based on screen size
  if (screenWidth >= 768 && screenWidth <= 1024) {
    return 'tablet';
  }
  
  // Desktop
  if (screenWidth > 1024) {
    return 'desktop';
  }
  
  return 'unknown';
};

// Detect operating system
export const detectOS = (): string => {
  const userAgent = navigator.userAgent;
  
  if (userAgent.indexOf('Windows') !== -1) return 'Windows';
  if (userAgent.indexOf('Mac') !== -1) return 'macOS';
  if (userAgent.indexOf('Linux') !== -1) return 'Linux';
  if (userAgent.indexOf('Android') !== -1) return 'Android';
  if (userAgent.indexOf('iOS') !== -1 || userAgent.indexOf('iPhone') !== -1 || userAgent.indexOf('iPad') !== -1) return 'iOS';
  
  return 'Unknown';
};

// Detect browser and version
export const detectBrowser = (): { browser: string; version: string } => {
  const userAgent = navigator.userAgent;
  
  // Chrome
  if (userAgent.indexOf('Chrome') !== -1 && userAgent.indexOf('Edg') === -1) {
    const version = userAgent.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
    return { browser: 'Chrome', version };
  }
  
  // Firefox
  if (userAgent.indexOf('Firefox') !== -1) {
    const version = userAgent.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
    return { browser: 'Firefox', version };
  }
  
  // Safari
  if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1) {
    const version = userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown';
    return { browser: 'Safari', version };
  }
  
  // Edge
  if (userAgent.indexOf('Edg') !== -1) {
    const version = userAgent.match(/Edg\/(\d+)/)?.[1] || 'Unknown';
    return { browser: 'Edge', version };
  }
  
  return { browser: 'Unknown', version: 'Unknown' };
};

// Get screen resolution
export const getScreenResolution = (): string => {
  return `${window.screen.width}x${window.screen.height}`;
};

// Get viewport size
export const getViewportSize = (): string => {
  return `${window.innerWidth}x${window.innerHeight}`;
};

// Detect wallet providers
export const detectWalletProviders = (): WalletType[] => {
  const providers: WalletType[] = [];
  
  // Check for MetaMask
  if (typeof window !== 'undefined' && (window as any).ethereum?.isMetaMask) {
    providers.push('metamask');
  }
  
  // Check for OKX
  if (typeof window !== 'undefined' && (window as any).okxwallet) {
    providers.push('okx');
  }
  
  // Check for Coinbase
  if (typeof window !== 'undefined' && (window as any).ethereum?.isCoinbaseWallet) {
    providers.push('coinbase');
  }
  
  // Check for Phantom (Solana)
  if (typeof window !== 'undefined' && (window as any).phantom?.solana) {
    providers.push('phantom');
  }
  
  // Check for Solflare (Solana)
  if (typeof window !== 'undefined' && (window as any).solflare) {
    providers.push('solflare');
  }
  
  // Check for WalletConnect
  if (typeof window !== 'undefined' && (window as any).ethereum?.isWalletConnect) {
    providers.push('walletconnect');
  }
  
  return providers.length > 0 ? providers : ['unknown'];
};

// Detect provider conflicts
export const detectProviderConflicts = (): string[] => {
  const conflicts: string[] = [];
  
  if (typeof window === 'undefined') return conflicts;
  
  const ethereum = (window as any).ethereum;
  
  if (ethereum) {
    // Check for multiple providers injecting into the same ethereum object
    if (ethereum.providers && Array.isArray(ethereum.providers)) {
      const providerNames = ethereum.providers.map((provider: any) => {
        if (provider.isMetaMask) return 'MetaMask';
        if (provider.isCoinbaseWallet) return 'Coinbase';
        if (provider.isWalletConnect) return 'WalletConnect';
        return 'Unknown';
      });
      
      if (providerNames.length > 1) {
        conflicts.push(`Multiple providers detected: ${providerNames.join(', ')}`);
      }
    }
    
    // Check for conflicting flags
    const flags = [];
    if (ethereum.isMetaMask) flags.push('MetaMask');
    if (ethereum.isCoinbaseWallet) flags.push('Coinbase');
    if (ethereum.isWalletConnect) flags.push('WalletConnect');
    
    if (flags.length > 1) {
      conflicts.push(`Conflicting provider flags: ${flags.join(', ')}`);
    }
  }
  
  return conflicts;
};

// Get wallet versions
export const getWalletVersions = (): Record<string, string> => {
  const versions: Record<string, string> = {};
  
  if (typeof window === 'undefined') return versions;
  
  // MetaMask version
  if ((window as any).ethereum?.isMetaMask) {
    versions.metamask = (window as any).ethereum.version || 'Unknown';
  }
  
  // OKX version
  if ((window as any).okxwallet) {
    versions.okx = (window as any).okxwallet.version || 'Unknown';
  }
  
  // Phantom version
  if ((window as any).phantom?.solana) {
    versions.phantom = (window as any).phantom.solana.version || 'Unknown';
  }
  
  return versions;
};

// Get network information
export const getNetworkInfo = async (): Promise<NetworkInfo> => {
  const networkInfo: NetworkInfo = {
    isConnected: navigator.onLine,
    connectionType: 'unknown',
    effectiveType: 'unknown'
  };
  
  // Get connection type if available
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    networkInfo.connectionType = connection.type || 'unknown';
    networkInfo.effectiveType = connection.effectiveType || 'unknown';
  }
  
  // Get chain ID if wallet is connected
  if (typeof window !== 'undefined' && (window as any).ethereum) {
    try {
      const chainId = await (window as any).ethereum.request({ method: 'eth_chainId' });
      networkInfo.chainId = parseInt(chainId, 16);
      
      // Map chain ID to network name
      const networkNames: Record<number, string> = {
        1: 'Ethereum Mainnet',
        5: 'Goerli Testnet',
        11155111: 'Sepolia Testnet',
        137: 'Polygon Mainnet',
        80001: 'Polygon Mumbai',
        56: 'BSC Mainnet',
        97: 'BSC Testnet',
        43114: 'Avalanche Mainnet',
        43113: 'Avalanche Fuji'
      };
      
      networkInfo.networkName = networkNames[networkInfo.chainId] || `Chain ${networkInfo.chainId}`;
    } catch (error) {
      // Wallet not connected or error getting chain ID
    }
  }
  
  return networkInfo;
};

// Get wallet information
export const getWalletInfo = (): WalletInfo => {
  const connectedWallets = detectWalletProviders();
  const providerConflicts = detectProviderConflicts();
  const walletVersions = getWalletVersions();
  
  return {
    connectedWallets,
    primaryWallet: connectedWallets[0] || undefined,
    hasMultipleWallets: connectedWallets.length > 1,
    providerConflicts,
    walletVersions
  };
};

// Get performance information
export const getPerformanceInfo = (): { memoryUsage?: number; connectionSpeed?: string } => {
  const performanceInfo: { memoryUsage?: number; connectionSpeed?: string } = {};
  
  // Memory usage (if available)
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    performanceInfo.memoryUsage = memory.usedJSHeapSize;
  }
  
  // Connection speed (if available)
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    performanceInfo.connectionSpeed = connection.effectiveType;
  }
  
  return performanceInfo;
};

// Get complete device information
export const getDeviceInfo = (): DeviceInfo => {
  const deviceType = detectDeviceType();
  const os = detectOS();
  const { browser, version } = detectBrowser();
  const screenResolution = getScreenResolution();
  const viewportSize = getViewportSize();
  
  return {
    type: deviceType,
    os,
    browser,
    browserVersion: version,
    screenResolution,
    viewportSize,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop'
  };
};

// Check if logging should be enabled based on environment
export const shouldEnableLogging = (): boolean => {
  // Don't log in development if explicitly disabled
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_ENABLE_LOGGING === 'false') {
    return false;
  }
  
  // Don't log in test environment
  if (process.env.NODE_ENV === 'test') {
    return false;
  }
  
  // Enable by default in production and development
  return process.env.REACT_APP_ENABLE_LOGGING !== 'false';
};

// Parse comma-separated environment variables
export const parseStringArray = (value: string | undefined): string[] => {
  if (!value || value.trim() === '') return [];
  return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
};

// Get current timestamp
export const getCurrentTimestamp = (): number => {
  return Date.now();
};

// Format timestamp for display
export const formatTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toISOString();
};

// Check if we're in a browser environment
export const isBrowser = (): boolean => {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
};

// Get current URL information
export const getUrlInfo = () => {
  if (!isBrowser()) {
    return {
      url: '',
      referrer: '',
      pathname: '',
      search: '',
      hash: ''
    };
  }
  
  return {
    url: window.location.href,
    referrer: document.referrer,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash
  };
};
